import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rpc } from "../../lib/rpc";
import { AdminPageHeader } from "../../components/admin/DataTable";
import { PhotoGallery } from "../../components/photo/PhotoGallery";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const REVIEW_LABELS = {
    new: "Neu",
    in_review: "In Prüfung",
    adopted: "Übernommen",
    not_adopted: "Nicht übernommen",
};

export default function AdminClarifications() {
    const [cases, setCases] = useState([]);
    const [sel, setSel] = useState(null);
    const [articles, setArticles] = useState([]);
    const [searchArt, setSearchArt] = useState("");
    const [adoptKind, setAdoptKind] = useState(null);

    const load = () =>
        Promise.all([
            rpc.get("/unknown-items"),
            rpc.get("/master/articles"),
        ]).then(([items, masterArticles]) => {
            if (items.ok) setCases(items.data.filter((item) => item.status === "open"));
            if (masterArticles.ok) setArticles(masterArticles.data);
        }).catch(() => {});

    useEffect(() => { load(); }, []);

    const resolve = async (resolution, targetArticleId, targetAssetId) => {
        if (!sel) return;
        const res = await rpc.post("/unknown-items/" + sel.id + "/resolve", {
            resolution,
            target_article_id: targetArticleId,
            target_asset_id: targetAssetId,
        });
        if (!res.ok) return toast.error(res.error);
        toast.success("Gegenstand bearbeitet.");
        setSel(null);
        load();
    };

    const startReview = async () => {
        if (!sel || sel.review_state === "in_review") return;
        const res = await rpc.post("/unknown-items/" + sel.id + "/start-review");
        if (!res.ok) return toast.error(res.error);
        setSel(res.data);
        load();
    };

    const filteredArticles = articles.filter((article) =>
        article.name.toLowerCase().includes(searchArt.toLowerCase())
    );

    return (
        <>
            <AdminPageHeader title="Offene Gegenstände" subtitle={cases.length + " offen"} />
            <div className="grid grid-cols-12 gap-6">
                <aside className="col-span-12 lg:col-span-4">
                    <h3 className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        Neu aufgenommen
                    </h3>
                    <div className="flex flex-col gap-2" data-testid="clar-case-list">
                        {cases.length === 0 ? (
                            <div className="rounded-md border border-border/60 bg-card/40 px-3 py-4 text-xs text-muted-foreground">
                                Keine offenen Gegenstände.
                            </div>
                        ) : (
                            cases.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setSel(item)}
                                    className={"rounded-md border px-3 py-2 text-left text-sm " + (sel?.id === item.id ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card/40 text-muted-foreground")}
                                    data-testid={"clar-case-" + item.id}
                                >
                                    <div className="font-medium text-foreground">
                                        {item.proposed_name || "Ohne Bezeichnung"}
                                    </div>
                                    <div className="text-xs">
                                        {[item.found_at_compartment_code, item.comment].filter(Boolean).join(" · ") || "Keine Zusatzangabe"}
                                    </div>
                                    <div className="mt-1 text-xs font-medium uppercase tracking-wider text-primary">
                                        {REVIEW_LABELS[item.review_state] || "Neu"}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </aside>

                <main className="col-span-12 lg:col-span-8">
                    {sel ? (
                        <div className="rounded-lg border border-border bg-card/40 p-5">
                            <h3 className="mb-1 text-lg font-semibold text-foreground">
                                {sel.proposed_name || "Gegenstand ohne Bezeichnung"}
                            </h3>
                            <p className="mb-4 text-xs text-muted-foreground">
                                Aufgenommen am {new Date(sel.created_at).toLocaleString("de-DE")}
                                {sel.found_at_compartment_code ? " · Fach " + sel.found_at_compartment_code : ""}
                                {sel.comment ? " · " + sel.comment : ""}
                            </p>

                            <div className="mb-4 flex items-center justify-between rounded-md border border-border/60 bg-card/20 px-3 py-2 text-xs">
                                <span>Bearbeitungsstand: <strong>{REVIEW_LABELS[sel.review_state] || "Neu"}</strong></span>
                                {(sel.review_state || "new") === "new" ? (
                                    <Button size="sm" variant="outline" onClick={startReview} data-testid="clar-start-review">
                                        Prüfung beginnen
                                    </Button>
                                ) : null}
                            </div>

                            <div className="mb-4 rounded-md border border-border/60 bg-card/20 p-3">
                                <Label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">
                                    Fotos
                                </Label>
                                <PhotoGallery
                                    entityType="unknown_item_case"
                                    entityId={sel.id}
                                    isAdmin={true}
                                    showFilters={false}
                                    testId="clar-gallery"
                                />
                            </div>

                            <div className="mb-5 rounded-md border border-border/60 bg-card/20 p-3">
                                <Label className="mb-2 block text-xs">Als bestehenden Artikel zuordnen</Label>
                                <Input
                                    value={searchArt}
                                    onChange={(event) => setSearchArt(event.target.value)}
                                    placeholder="Artikel suchen..."
                                    className="mb-2 h-10"
                                    data-testid="clar-article-search"
                                />
                                <div className="max-h-40 overflow-auto rounded border border-border/60">
                                    {filteredArticles.slice(0, 30).map((article) => (
                                        <button
                                            key={article.id}
                                            type="button"
                                            onClick={() => resolve("article_matched", article.id, null)}
                                            className="flex w-full items-center justify-between border-t border-border/40 px-3 py-2 text-xs first:border-t-0 hover:bg-card/60"
                                            data-testid={"clar-match-" + article.id}
                                        >
                                            <span>{article.name}</span>
                                            <span className="text-muted-foreground">{article.type}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-5">
                                <Label className="mb-2 block text-xs">Neu anlegen</Label>
                                <div className="flex flex-wrap gap-2">
                                    <Button onClick={() => setAdoptKind("material")} data-testid="clar-adopt-material">
                                        Materialartikel anlegen
                                    </Button>
                                    <Button variant="outline" onClick={() => setAdoptKind("loan_item")} data-testid="clar-adopt-loan">
                                        Ausleihgegenstand anlegen
                                    </Button>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => resolve("discarded", null, null)} data-testid="clar-discard">
                                    Nicht übernehmen
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-xs text-muted-foreground">
                            Wähle links einen Gegenstand.
                        </div>
                    )}
                </main>
            </div>

            <AdoptItemDialog
                item={sel}
                kind={adoptKind}
                onClose={() => setAdoptKind(null)}
                onDone={() => {
                    setAdoptKind(null);
                    setSel(null);
                    load();
                }}
            />
        </>
    );
}

function AdoptItemDialog({ item, kind, onClose, onDone }) {
    const [form, setForm] = useState({
        name: "",
        ean: "",
        manufacturerNumber: "",
        minQuantity: "",
        assetTag: "",
        serialNumber: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!kind) return;
        setForm({
            name: item?.proposed_name || "",
            ean: "",
            manufacturerNumber: "",
            minQuantity: "",
            assetTag: "",
            serialNumber: "",
            notes: "",
        });
    }, [item, kind]);

    const submit = async () => {
        if (!item || !form.name.trim()) return toast.error("Bezeichnung ist Pflicht.");
        setBusy(true);
        const res = await rpc.post("/unknown-items/" + item.id + "/adopt", {
            kind,
            name: form.name.trim(),
            ean: form.ean.trim() || undefined,
            manufacturer_number: form.manufacturerNumber.trim() || undefined,
            min_quantity: form.minQuantity ? Number(form.minQuantity) : undefined,
            asset_tag: form.assetTag.trim() || undefined,
            serial_number: form.serialNumber.trim() || undefined,
            notes: form.notes.trim() || undefined,
        });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success(kind === "loan_item" ? "Ausleihgegenstand angelegt." : "Materialartikel angelegt. Bestand über Wareneingang buchen.");
        onDone();
    };

    const loanItem = kind === "loan_item";
    return (
        <Dialog open={Boolean(kind)} onOpenChange={(open) => !open && onClose()}>
            <DialogContent data-testid="clar-adopt-dialog">
                <DialogHeader>
                    <DialogTitle>{loanItem ? "Ausleihgegenstand anlegen" : "Materialartikel anlegen"}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-3">
                    <div>
                        <Label>Bezeichnung</Label>
                        <Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="clar-adopt-name" />
                    </div>
                    <div>
                        <Label>EAN (optional)</Label>
                        <Input value={form.ean} onChange={(event) => setForm({ ...form, ean: event.target.value })} />
                    </div>
                    <div>
                        <Label>Hersteller-Nr. (optional)</Label>
                        <Input value={form.manufacturerNumber} onChange={(event) => setForm({ ...form, manufacturerNumber: event.target.value })} />
                    </div>
                    {loanItem ? (
                        <>
                            <div>
                                <Label>Interne Kennung (optional)</Label>
                                <Input value={form.assetTag} onChange={(event) => setForm({ ...form, assetTag: event.target.value })} placeholder="Automatisch, wenn leer" />
                            </div>
                            <div>
                                <Label>Seriennummer (optional)</Label>
                                <Input value={form.serialNumber} onChange={(event) => setForm({ ...form, serialNumber: event.target.value })} />
                            </div>
                            <div>
                                <Label>Notiz (optional)</Label>
                                <Input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
                            </div>
                        </>
                    ) : (
                        <div>
                            <Label>Mindestbestand (optional)</Label>
                            <Input type="number" min="0" value={form.minQuantity} onChange={(event) => setForm({ ...form, minQuantity: event.target.value })} />
                        </div>
                    )}
                    <Button onClick={submit} disabled={busy} data-testid="clar-adopt-submit">
                        {busy ? "Wird angelegt..." : "Anlegen"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
